import React, { useState, useId, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  behaviorSettingsProps,
  generateClassNameByBehaviorSettings,
} from '../../../../utils/behaviorSettings';
import { scrollToElement } from '../../../../utils/scroll-to';
import ChevronDown from '../../../../public/static/icons/navigation-chevron-down.svg';
import FormattedText from '../../../01_atoms/FormattedText';

import styles from './index.module.scss';

const BBAccordion = ({ tabs, behaviorSettings = null, uuid = null }) => {
  const [activeTab, setActiveTab] = useState(0);
  const uniqueID = useId().replace(/:/g, '');

  // Scroll to tab when activeTab changes.
  const scrollToTabTitle = useCallback(() => {
    if (activeTab === -1) return;

    const openedTabID = `tab-${uniqueID}-${activeTab}`;
    const openedTab = document.getElementById(openedTabID);

    // Scroll to the clicked tab ONLY if the opened tab is now above the
    // window, and therefore we can't see the start of the tab.
    if (openedTab && window.pageYOffset > openedTab.offsetTop) {
      scrollToElement({
        elementId: openedTabID,
        offset: 0,
      });
    }
  }, [activeTab, uniqueID]);

  useEffect(() => {
    scrollToTabTitle();
  }, [activeTab, scrollToTabTitle]);

  const expandTab = useCallback(
    (tab, event) => {
      setActiveTab((prevTab) => (prevTab === tab ? -1 : tab));
      if (activeTab !== tab) {
        event.stopPropagation();
      }
    },
    [activeTab],
  );

  const handleClosedClick = useCallback(
    (tab, event) => {
      if (activeTab !== tab) {
        expandTab(tab, event);
      }
    },
    [activeTab, expandTab],
  );

  const classes = [
    'bb',
    'bb-accordion',
    styles['bb-accordion'],
    generateClassNameByBehaviorSettings(behaviorSettings),
  ];

  return (
    <div className={classes.join(' ')} id={uuid}>
      <div className="container">
        {tabs.map((tab, index) => (
          <div
            id={`tab-${uniqueID}-${index}`}
            className={`bb-accordion--tab tab${activeTab === index ? ' active' : ''}`}
            key={tab.title}
            onKeyPress={(event) => expandTab(index, event)}
            onClick={(event) => handleClosedClick(index, event)}
          >
            <div
              className={`toggler d-none d-md-block ${activeTab === index ? 'active' : 'closed'}`}
              onClick={(event) => expandTab(index, event)}
            >
              <ChevronDown />
            </div>

            {tab.title && (
              <p className="title">
                <strong>{tab.title}</strong>
              </p>
            )}

            <div className={`toggler d-md-none ${activeTab === index ? 'active' : 'closed'}`}>
              <ChevronDown />
            </div>

            <div className={`content ${activeTab === index ? '' : 'hidden'}`}>
              {tab?.image.url && (
                <div className="image">
                  <img
                    className="d-lg-none"
                    src={tab.image.url}
                    alt={tab.image.alt}
                    width={260}
                    height={152}
                  />
                </div>
              )}

              {tab.content && <FormattedText className="text" text={tab.content} />}

              {tab?.image.url && (
                <div className="image">
                  <img
                    className="d-none d-lg-block"
                    src={tab.image.url}
                    alt={tab.image.alt}
                    width={290}
                    height={170}
                  />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

BBAccordion.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      content: PropTypes.string.isRequired,
      image: PropTypes.shape({
        url: PropTypes.string,
        alt: PropTypes.string,
      }),
    }),
  ).isRequired,
  behaviorSettings: behaviorSettingsProps,
  uuid: PropTypes.string,
};

export default BBAccordion;
